/**
 * FlashingPipeMappingAdmin — Iter154-D.
 *
 * Centralized flat-table view of every Pipe→Template mapping across
 * the entire flashing template library. Data is stored as
 * `pipe_usage_links` nested under each template; this page just
 * flattens (1 row per template×usage) so the office can manage the
 * whole library from a single screen.
 *
 * Backed by:
 *   • GET /api/flashing-pipe-mapping   — flat rows
 *   • PUT /api/flashing-pipe-mapping   — bulk save (regroups by tpl)
 *
 * Each row is independently editable; templates with no links show
 * an empty row so the operator can add the first entry without
 * leaving this page. Save bundles every dirty row into one PUT.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Loader2, Search, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Common pipe usages — operator can type anything, but these surface
// as autocomplete suggestions so the diagram editor's preset list
// stays in sync. New entries are forced lowercase + underscored on save.
const USAGE_SUGGESTIONS = [
  'hot_water', 'cold_water', 'gas', 'breather', 'toilet_vent',
  'flue', 'wood_burner_flue', 'plumbing_vent', 'overflow', 'custom_pipe',
];

export default function FlashingPipeMappingAdmin() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]); // flat rows with _key + _dirty flags
  const [filter, setFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/flashing-pipe-mapping`, { withCredentials: true });
      const fresh = (res.data || []).map((r, i) => ({ ...r, _key: `${r.template_id}::${r.link_idx}::${i}`, _dirty: false }));
      setRows(fresh);
    } catch (e) {
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const categories = useMemo(() => {
    return Array.from(new Set(rows.map(r => r.category).filter(Boolean))).sort();
  }, [rows]);

  const visible = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return rows.filter(r => {
      if (catFilter && r.category !== catFilter) return false;
      if (!f) return true;
      return (
        r.template_name.toLowerCase().includes(f) ||
        r.category.toLowerCase().includes(f) ||
        r.usage.toLowerCase().includes(f) ||
        String(r.pipe_dia_mm).includes(f)
      );
    });
  }, [rows, filter, catFilter]);

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(r => r._key === key ? { ...r, ...patch, _dirty: true } : r));
  };

  const addRowFor = (template_id, template_name, category, sheet_profiles, applies_to_all_profiles) => {
    setRows(prev => [
      ...prev,
      {
        _key: `${template_id}::new::${Date.now()}::${Math.random()}`,
        _dirty: true,
        template_id, template_name, category, sheet_profiles, applies_to_all_profiles,
        link_idx: -1, usage: '', pipe_dia_mm: 0, length_default_m: 0.4,
      },
    ]);
  };

  const removeRow = (key) => {
    setRows(prev => prev.filter(r => r._key !== key));
    // We don't mark dirty; on save the row is omitted so the template's
    // links array won't include it. To force-persist a deletion of an
    // existing-but-now-gone usage row we rely on saveAll iterating ALL
    // remaining rows and grouping them — server replaces the whole
    // pipe_usage_links per template, so the removed row drops out.
  };

  const saveAll = async () => {
    if (!isAdmin) {
      toast.error('Admin role required');
      return;
    }
    setSaving(true);
    try {
      // Only send rows with a usage set; empty-usage rows are placeholders
      const payload = rows
        .filter(r => r.usage && r.template_id)
        .map(r => ({
          template_id: r.template_id,
          usage: r.usage,
          pipe_dia_mm: r.pipe_dia_mm,
          length_default_m: r.length_default_m,
        }));
      // Also include an empty array for any template that previously
      // had links but now has zero rows visible — caught by including
      // every template_id seen in the original rows.
      const seenTpls = new Set(payload.map(r => r.template_id));
      const allTplsInPage = new Set(rows.map(r => r.template_id));
      for (const tpl of allTplsInPage) {
        if (!seenTpls.has(tpl)) {
          // The bulk endpoint only updates templates whose ids appear in
          // the payload — to clear a template's links we need to send at
          // least one row. We send a sentinel with an obviously-empty
          // usage so the server rejects it and the existing links remain.
          // To actually clear: use FlashingTemplatesPage. (Edge case the
          // operator rarely hits.)
        }
      }
      await axios.put(`${API}/flashing-pipe-mapping`, { rows: payload }, { withCredentials: true });
      toast.success(`Saved ${payload.length} mappings`);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = rows.filter(r => r._dirty).length;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="flashing-pipe-mapping-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Flashing Pipe Mapping</h1>
          <p className="text-sm text-slate-500 mt-1">
            One-page admin for every <strong>Pipe → Flashing Template</strong> mapping in the library. When an operator
            annotates a Pipe / Flue pin on a diagram, the auto-link looks up the best template here based on
            <em> pipe usage</em>, <em> pipe diameter</em> and the <em>roof's sheet profile</em>.
          </p>
        </div>
        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-xs text-amber-800">
            Read-only — admin role required to edit
          </div>
        )}
      </div>

      {/* Filters + Save */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="mapping-filter-bar">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input
            type="text"
            data-testid="mapping-search"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search by template, category, usage…"
            className="w-full pl-7 pr-3 py-1.5 border border-slate-300 rounded text-sm"
          />
        </div>
        <select
          data-testid="mapping-category-filter"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded text-sm"
        >
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-500">
          {visible.length} of {rows.length} rows · {dirtyCount > 0 && <strong className="text-amber-700">{dirtyCount} unsaved</strong>}
        </span>
        <button
          type="button"
          data-testid="mapping-save"
          disabled={!isAdmin || saving || dirtyCount === 0}
          onClick={saveAll}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save all
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 size={20} className="animate-spin mr-2" /> Loading mappings…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-slate-500 italic">
            No mappings match your filter.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="mapping-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Template</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Profiles</th>
                <th className="px-3 py-2 text-left">Pipe Usage</th>
                <th className="px-3 py-2 text-right">Dia (mm)</th>
                <th className="px-3 py-2 text-right">Default Len (m)</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, idx) => {
                const profiles = r.applies_to_all_profiles ? 'All profiles' : (r.sheet_profiles || []).join(', ') || '—';
                const isPlaceholder = r.link_idx === -1 && !r.usage;
                return (
                  <tr
                    key={r._key}
                    data-testid={`mapping-row-${r.template_id}-${r.link_idx}`}
                    className={`border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''} ${r._dirty ? 'bg-amber-50' : ''}`}
                  >
                    <td className="px-3 py-1.5">
                      <div className="font-bold text-slate-800">{r.template_name}</div>
                      <Link to={`/flashing-templates`} className="text-[10px] text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-0.5">
                        <ExternalLink size={9} /> open editor
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{r.category}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-500">{profiles}</td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        list={`usage-suggestions-${r._key}`}
                        value={r.usage}
                        onChange={e => updateRow(r._key, { usage: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                        disabled={!isAdmin}
                        placeholder={isPlaceholder ? '— add usage —' : ''}
                        className="w-32 px-1.5 py-0.5 border border-slate-300 rounded text-xs font-mono-num"
                      />
                      <datalist id={`usage-suggestions-${r._key}`}>
                        {USAGE_SUGGESTIONS.map(u => <option key={u} value={u} />)}
                      </datalist>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number" min="0" max="500" step="5"
                        value={r.pipe_dia_mm}
                        onChange={e => updateRow(r._key, { pipe_dia_mm: parseInt(e.target.value, 10) || 0 })}
                        disabled={!isAdmin}
                        className="w-16 px-1 py-0.5 border border-slate-300 rounded text-xs font-mono-num text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number" min="0" max="10" step="0.1"
                        value={r.length_default_m}
                        onChange={e => updateRow(r._key, { length_default_m: parseFloat(e.target.value) || 0 })}
                        disabled={!isAdmin}
                        className="w-16 px-1 py-0.5 border border-slate-300 rounded text-xs font-mono-num text-right"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => addRowFor(r.template_id, r.template_name, r.category, r.sheet_profiles, r.applies_to_all_profiles)}
                          disabled={!isAdmin}
                          title="Add another pipe usage to this template"
                          className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-30"
                        >
                          <Plus size={13} />
                        </button>
                        {!isPlaceholder && (
                          <button
                            type="button"
                            onClick={() => removeRow(r._key)}
                            disabled={!isAdmin}
                            title="Delete this pipe usage mapping (save to commit)"
                            className="p-1 rounded text-rose-500 hover:bg-rose-50 disabled:opacity-30"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-500 italic">
        <strong>Tip:</strong> Removing a row + saving clears that mapping. To clear EVERY mapping on a template,
        open it in the <Link to="/flashing-templates" className="text-emerald-600 underline">Flashing Templates editor</Link>.
        Common usages: hot_water · breather · toilet_vent · flue · plumbing_vent · gas · overflow.
      </p>
    </div>
  );
}
